import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { Menu, X, ChevronDown } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import logoWhiteLong from "@/assets/logo-white-long.png";
import logoWhiteIso from "@/assets/logo-white-iso.png";

const mainNavItems = [
  { key: "nav.nosotros", href: "#nosotros", color: "#E92052" },
  { key: "nav.servicios", href: "#servicios", color: "#E92052" },
  { key: "nav.metodologia", href: "#metodologia", color: "#fda700" },
];

const moreNavItems = [
  { key: "nav.ia", href: "#ia", color: "#fda700" },
  { key: "nav.hub", href: "#hub", color: "#602d90" },
  { key: "nav.equipo", href: "#equipo", color: "#602d90" },
  { key: "nav.hablemos", href: "#contacto", color: "#E92052" },
];

const allNavItems = [...mainNavItems, ...moreNavItems];
const sectionIds = allNavItems.map((item) => item.href.replace("#", ""));

const Navbar = () => {
  const { scrollY } = useScroll();
  const bgOpacity = useTransform(scrollY, [0, 100], [0, 0.85]);
  const { lang, setLang, t } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const moreRef = useRef<HTMLDivElement>(null);

  const rafRef = useRef<number | null>(null);

  const detectSection = useCallback(() => {
    const offset = 120;
    for (let i = sectionIds.length - 1; i >= 0; i--) {
      const el = document.getElementById(sectionIds[i]);
      if (el) {
        const top = el.getBoundingClientRect().top;
        if (top <= offset) {
          setActiveSection(sectionIds[i]);
          return;
        }
      }
    }
    setActiveSection(null);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        detectSection();
        rafRef.current = null;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    detectSection();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [detectSection]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getSectionY = (href: string) => {
    const el = document.querySelector<HTMLElement>(href);
    if (!el) return null;
    return Math.max(0, el.getBoundingClientRect().top + window.scrollY - 72);
  };

  const scrollToTop = (e: React.MouseEvent) => {
    e.preventDefault();
    window.history.replaceState(null, "", window.location.pathname);
    window.scrollTo({ top: 0, behavior: "smooth" });
    window.setTimeout(() => window.scrollTo({ top: 0, behavior: "auto" }), 500);
  };

  const scrollToSection = (href: string) => {
    const initialY = getSectionY(href);
    if (initialY === null) return;

    window.history.replaceState(null, "", href);
    window.scrollTo({ top: initialY, behavior: "smooth" });

    const settleScroll = () => {
      const finalY = getSectionY(href);
      if (finalY !== null) {
        window.scrollTo({ top: finalY, behavior: "auto" });
      }
    };

    window.setTimeout(settleScroll, 350);
    window.setTimeout(settleScroll, 850);
  };

  const isMoreActive = moreNavItems.some(
    (item) => activeSection === item.href.replace("#", "")
  );

  return (
    <>
      <motion.nav
        initial={{ y: -100, opacity: 0, filter: "blur(10px)" }}
        animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
        transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-4 left-4 right-4 z-50 rounded-2xl px-6 py-3"
        style={{
          backgroundColor: useTransform(bgOpacity, (v) => `rgba(13,13,13,${v})`),
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="#" onClick={scrollToTop} className="flex items-center">
            <img src={logoWhiteLong} alt="Yamper Grow" className="hidden lg:block h-12" width={92} height={48} fetchPriority="high" />
            <img src={logoWhiteIso} alt="Yamper Grow" className="block lg:hidden h-10" width={40} height={40} fetchPriority="high" />
          </a>

          <div className="hidden lg:flex items-center gap-7">
            {mainNavItems.map((item) => {
              const sectionId = item.href.replace("#", "");
              const isActive = activeSection === sectionId;
              return (
                <a
                  key={item.key}
                  href={item.href}
                  className="text-[11px] uppercase tracking-[0.18em] font-semibold transition-colors duration-300"
                  style={{ color: isActive ? item.color : "rgba(255,255,255,0.5)" }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = "#E92052"; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = "rgba(255,255,255,0.5)"; }}
                  onClick={(e) => { e.preventDefault(); scrollToSection(item.href); }}
                >
                  {t(item.key)}
                </a>
              );
            })}

            {/* Más dropdown */}
            <div ref={moreRef} className="relative">
              <button
                className="flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] font-semibold transition-colors duration-300"
                style={{ color: isMoreActive ? "#602d90" : "rgba(255,255,255,0.5)" }}
                onMouseEnter={(e) => { if (!isMoreActive) e.currentTarget.style.color = "#E92052"; }}
                onMouseLeave={(e) => { if (!isMoreActive && !moreOpen) e.currentTarget.style.color = "rgba(255,255,255,0.5)"; }}
                onClick={() => setMoreOpen(!moreOpen)}
              >
                {t("nav.mas") || "Más"}
                <ChevronDown size={12} className={`transition-transform duration-200 ${moreOpen ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
                {moreOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.18 }}
                    className="absolute top-full mt-3 left-1/2 -translate-x-1/2 min-w-[160px] rounded-xl py-2 px-1"
                    style={{
                      backgroundColor: "rgba(13,13,13,0.95)",
                      backdropFilter: "blur(20px)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    {moreNavItems.map((item) => {
                      const sectionId = item.href.replace("#", "");
                      const isActive = activeSection === sectionId;
                      return (
                        <a
                          key={item.key}
                          href={item.href}
                          className="block px-4 py-2.5 text-[11px] uppercase tracking-[0.18em] font-semibold rounded-lg transition-colors duration-200 hover:bg-white/5"
                          style={{ color: isActive ? item.color : "rgba(255,255,255,0.5)" }}
                          onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = "#E92052"; }}
                          onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = "rgba(255,255,255,0.5)"; }}
                          onClick={(e) => {
                            e.preventDefault();
                            setMoreOpen(false);
                            scrollToSection(item.href);
                          }}
                        >
                          {t(item.key)}
                        </a>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden lg:flex items-center gap-1.5">
              <button
                onClick={() => setLang("es")}
                className={`w-7 h-7 rounded-full text-[10px] font-bold uppercase border transition-all duration-300 ${lang === "es" ? "bg-brand-rosa border-brand-rosa text-white text-[11px]" : "bg-transparent border-white/15 text-white/50 hover:text-white/60"}`}
              >
                ES
              </button>
              <button
                onClick={() => setLang("en")}
                className={`w-7 h-7 rounded-full text-[10px] font-bold uppercase border transition-all duration-300 ${lang === "en" ? "bg-brand-rosa border-brand-rosa text-white text-[11px]" : "bg-transparent border-white/15 text-white/50 hover:text-white/60"}`}
              >
                EN
              </button>
            </div>
            <a
              href="#contacto"
              className="px-5 py-2 rounded-full bg-brand-rosa text-white text-[12px] uppercase tracking-[0.12em] font-bold hover:bg-brand-rosa/80 transition-colors"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("#contacto");
              }}
            >
              {t("nav.hablemos")}
            </a>
            <button
              className="group lg:hidden text-white/60 hover:text-brand-rosa transition-colors duration-300"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <motion.div
          className="fixed inset-0 z-40 bg-foreground/95 backdrop-blur-xl flex flex-col items-center justify-center gap-6 pt-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {allNavItems.map((item) => {
            const sectionId = item.href.replace("#", "");
            const isActive = activeSection === sectionId;
            return (
              <a
                key={item.key}
                href={item.href}
                className="text-xl font-bold transition-colors"
                style={{ color: isActive ? item.color : "rgba(255,255,255,0.7)" }}
                onClick={(e) => {
                  e.preventDefault();
                  setMobileOpen(false);
                  scrollToSection(item.href);
                }}
              >
                {t(item.key)}
              </a>
            );
          })}
          <div className="flex items-center gap-3 mt-4">
            <button onClick={() => { setLang("es"); setMobileOpen(false); }} className={`px-4 py-2 rounded-full text-sm font-bold ${lang === "es" ? "bg-brand-rosa text-white" : "text-white/40"}`}>ES</button>
            <button onClick={() => { setLang("en"); setMobileOpen(false); }} className={`px-4 py-2 rounded-full text-sm font-bold ${lang === "en" ? "bg-brand-rosa text-white" : "text-white/40"}`}>EN</button>
          </div>
        </motion.div>
      )}
    </>
  );
};

export default Navbar;
